import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, ArrowRight, MapPin, Building2, Quote } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import SEO from "../components/SEO";
import SectionReveal from "../components/SectionReveal";
import { publicApi } from "../lib/adminApi";
import { trackEvent } from "../lib/analytics";
import NotFound from "./NotFound";

const SITE_URL = "https://ridgehawk.ca";

export default function CaseStudyDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setNotFound(false); setData(null);
    (async () => {
      const res = await publicApi.getCase(slug);
      if (cancelled) return;
      if (res.ok) {
        setData(res.data);
        const cs = res.data?.case_study;
        if (cs?.slug) {
          trackEvent("view_item", {
            item_id: cs.slug,
            item_name: cs.title,
            item_category: "case_study",
          });
        }
      } else if (res.status === 404) setNotFound(true);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) return <p className="container-pad py-32 text-sm text-ridge-muted">Loading…</p>;
  if (notFound || !data) return <NotFound />;
  const { case_study: cs, related } = data;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: cs.title,
    description: cs.excerpt || cs.seo_description,
    image: cs.cover_image_url ? `${SITE_URL}${cs.cover_image_url.startsWith("http") ? "" : ""}${cs.cover_image_url}` : `${SITE_URL}/logo.jpeg`,
    datePublished: cs.published_at,
    dateModified: cs.updated_at || cs.published_at,
    author: { "@type": "Organization", name: "Ridgehawk Logistics" },
    publisher: { "@type": "Organization", name: "Ridgehawk Logistics", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.jpeg` } },
    mainEntityOfPage: `${SITE_URL}/case-studies/${cs.slug}`,
  };
  const breadcrumbSchema = {
    "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Case Studies", item: `${SITE_URL}/case-studies` },
      { "@type": "ListItem", position: 3, name: cs.title, item: `${SITE_URL}/case-studies/${cs.slug}` },
    ],
  };

  return (
    <>
      <SEO title={cs.seo_title || cs.title} description={cs.seo_description || cs.excerpt} path={`/case-studies/${cs.slug}`} />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {cs.cover_image_url ? (
        <div className="relative h-[42vh] min-h-[360px] w-full overflow-hidden bg-ridge-charcoal" data-testid="case-cover">
          <img src={cs.cover_image_url} alt={cs.cover_image_alt || ""} className="h-full w-full object-cover opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-t from-ridge-charcoal/60 to-transparent" />
        </div>
      ) : <div className="h-16 bg-ridge-charcoal" />}

      <article className="bg-white">
        <div className="container-pad py-12 md:py-16 lg:py-20">
          <SectionReveal>
            <div className="mx-auto max-w-3xl">
              <Link to="/case-studies" className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.16em] text-ridge-amber hover:text-ridge-amber-dark">
                <ArrowLeft className="h-3.5 w-3.5" /> Back to case studies
              </Link>
              <div className="mt-5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs font-semibold uppercase tracking-[0.18em] text-ridge-muted">
                {cs.client_name ? <span className="text-ridge-charcoal" data-testid="case-client-name">{cs.client_name}</span> : null}
                {cs.client_industry ? <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" />{cs.client_industry}</span> : null}
                {cs.client_location ? <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{cs.client_location}</span> : null}
                {cs.service_type ? <span>{cs.service_type}</span> : null}
              </div>
              <h1 className="font-display mt-4 text-4xl font-bold leading-[1.08] tracking-tight text-ridge-charcoal sm:text-5xl">
                {cs.title}
              </h1>
              {cs.excerpt ? <p className="mt-5 max-w-2xl text-base leading-relaxed text-ridge-text md:text-lg">{cs.excerpt}</p> : null}
            </div>
          </SectionReveal>

          {/* Metrics row */}
          {(cs.metrics || []).length > 0 ? (
            <SectionReveal>
              <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-px overflow-hidden rounded-md border border-ridge-border bg-ridge-border sm:grid-cols-2 md:grid-cols-3" data-testid="case-metrics">
                {cs.metrics.map((m, i) => (
                  <div key={i} className="bg-white p-6 md:p-7">
                    <div className="text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-ridge-muted">{m.label}</div>
                    <div className="font-display mt-2 text-3xl font-bold text-ridge-amber md:text-4xl">{m.value}</div>
                  </div>
                ))}
              </div>
            </SectionReveal>
          ) : null}

          {/* Sections */}
          <div className="mx-auto mt-14 max-w-[720px] space-y-12">
            <Section title="The Challenge" body={cs.challenge} testId="case-challenge" />
            <Section title="Our Solution" body={cs.solution} testId="case-solution" />
            <Section title="The Results" body={cs.results} testId="case-results" />
          </div>

          {cs.testimonial_quote ? (
            <SectionReveal>
              <figure className="mx-auto mt-16 max-w-3xl rounded-md border-l-4 border-ridge-amber bg-ridge-stone p-8 md:p-10" data-testid="case-testimonial">
                <Quote className="h-7 w-7 text-ridge-amber" aria-hidden="true" />
                <blockquote className="font-display mt-4 text-xl font-medium leading-relaxed text-ridge-charcoal md:text-2xl">
                  "{cs.testimonial_quote}"
                </blockquote>
                {cs.testimonial_author ? (
                  <figcaption className="mt-5 text-xs font-semibold uppercase tracking-[0.18em] text-ridge-muted">
                    {cs.testimonial_author}
                  </figcaption>
                ) : null}
              </figure>
            </SectionReveal>
          ) : null}
        </div>
      </article>

      <section className="bg-ridge-amber">
        <div className="container-pad py-14 md:py-20">
          <div className="flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-center">
            <h2 className="font-display max-w-2xl text-2xl font-bold tracking-tight text-white md:text-3xl">
              Want this kind of outcome for your operation?
            </h2>
            <Link to="/quote" className="inline-flex items-center justify-center gap-2 rounded-md bg-ridge-charcoal px-7 py-3.5 text-sm font-semibold uppercase tracking-[0.08em] text-white hover:bg-black">
              Get a Quote <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {related?.length ? (
        <section className="section-pad bg-white" data-testid="case-related">
          <div className="container-pad">
            <h2 className="font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">More case studies</h2>
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
              {related.map((r) => (
                <Link key={r.id} to={`/case-studies/${r.slug}`} className="group block overflow-hidden rounded-md border border-ridge-border bg-white transition-colors hover:border-ridge-charcoal/25">
                  <div className="aspect-[16/10] w-full overflow-hidden bg-ridge-stone">
                    {r.cover_image_url ? <img src={r.cover_image_url} alt={r.cover_image_alt || ""} className="h-full w-full object-cover" /> : <div className="h-full w-full bg-gradient-to-br from-ridge-charcoal to-ridge-amber-dark" />}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-base font-semibold leading-snug text-ridge-charcoal group-hover:text-ridge-amber-dark">{r.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      ) : null}
    </>
  );
}

function Section({ title, body, testId }) {
  if (!body || !body.trim()) return null;
  return (
    <SectionReveal>
      <section data-testid={testId}>
        <h2 className="font-display text-2xl font-bold tracking-tight text-ridge-charcoal md:text-3xl">{title}</h2>
        <span className="accent-rule mt-3 block" />
        <div className="prose-ridge mt-6 max-w-none text-base leading-[1.85] text-ridge-text md:text-lg">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
        </div>
      </section>
    </SectionReveal>
  );
}
